/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Centralized schema definitions and utility functions for claim status searching and filtering.
 */
import _ from 'lodash';
import { JsonRule, JsonTree, Utils, ValueSource, Config, JsonGroup, Field } from '@react-awesome-query-builder/antd';

import { CORE_CONFIG, IFieldsData, QUERY_BUILDER_FILTER_TYPE } from 'lib/SearchQueryBuilder/config/entity.config';
import { OPERATORS } from 'lib/SearchQueryBuilder/config/queryConfig';
import { buildFieldToAdf, CRITERIA_KEYS, type CriteriaKey } from './adfUtils';
import { hasNonEmptyValue } from '@veritable/common/utils';

const getRqbBaseType = (fieldKey: string, fieldsDefinitions: IFieldsData[]): string => {
	const fieldDef = _.find(fieldsDefinitions, (f) => f?.field === fieldKey);
	if (fieldDef) {
		const uiFieldType = fieldDef.ui_field_type;
		const coreConfigEntry = CORE_CONFIG[uiFieldType];
		if (coreConfigEntry?.getField) {
			return coreConfigEntry.getField({ display_name: fieldKey } as AnyType)?.type;
		}
	}
	return 'text';
};

const filterQuery = (query: JsonTree): AnyType => {
	let result: JsonGroup['children1'] = [];

	const children1 = query?.children1 as AnyType;
	_.forEach(children1, (values) => {
		if (!_.isEmpty(values?.children1)) {
			const childResult = filterQuery(values);
			if (!_.isEmpty(childResult)) {
				result = _.concat(result, childResult);
			}
			return;
		}

		const { field, value, operator, valueSrc } = values?.properties || {};
		if (field !== null && !_.isNil(operator) && _.size(_.without(value, undefined, null)) === _.size(valueSrc)) {
			result = _.concat(result, values);
		}
	});

	if (_.isEmpty(result)) {
		return {};
	}

	return {
		...query,
		children1: result,
	};
};

/**
 * Build a criteria object from a query-builder tree using a field->ADF map.
 * Returns an object under `criteria` containing all requested ADF groups.
 */
const buildCriteria = (
	query: JsonTree | null,
	adfFieldMap: Partial<Record<CriteriaKey, string[]>>,
): Record<CriteriaKey, JsonGroup> | null => {
	if (!query) {
		return null;
	}

	const fieldToAdfMap = buildFieldToAdf(adfFieldMap);
	const adfKeys = _.keys(adfFieldMap) as CriteriaKey[];
	const buckets: Partial<Record<CriteriaKey, JsonGroup>> = {};

	const createBucket = (adfKey: CriteriaKey) => {
		if (!buckets[adfKey]) {
			buckets[adfKey] = {
				id: Utils.uuid(),
				type: 'group',
				children1: [],
				properties: { conjunction: 'AND', not: false },
			};
		}
	};
	// Seeding all ADF keys so that groups exist even if they are empty
	_.forEach(adfKeys, createBucket);
	const visit = (node: AnyType) => {
		if (!node) {
			return;
		}
		if (node.type === 'rule') {
			const field = node?.properties?.field;
			const adfKey = field && fieldToAdfMap[field] ? fieldToAdfMap[field] : CRITERIA_KEYS.criteria;
			buckets[adfKey].children1.push(node);
			return;
		}
		if (node.type === 'group' && node.children1) {
			const children = node.children1;
			if (_.isArray(children) || _.isObject(children)) {
				_.forEach(children, visit);
			}
		}
	};

	visit(query);
	const pairs: [CriteriaKey, JsonGroup][] = [];
	_.forEach(adfKeys, (key) => {
		const bucket = buckets[key];
		if (bucket && !_.isEmpty(bucket.children1)) {
			pairs.push([key, bucket]);
		} else if (key === CRITERIA_KEYS.criteria) {
			pairs.push([key, null]);
		}
	});
	return _.fromPairs(pairs) as Record<CriteriaKey, JsonGroup>;
};

const getDefaultQuery = (config: Config): JsonTree => {
	const firstConfig = _.first(_.keys(config?.fields));
	const fieldConfig = config?.fields?.[firstConfig] as Field;
	const defaultOperator = fieldConfig?.defaultOperator ?? _.first(fieldConfig?.operators) ?? null;

	return {
		id: Utils.uuid(),
		type: 'group',
		properties: { conjunction: 'OR', not: false },
		children1: [
			{
				type: 'group',
				id: Utils.uuid(),
				properties: {
					conjunction: 'AND',
					not: false,
				},
				children1: [
					{
						type: 'rule',
						id: Utils.uuid(),
						properties: {
							fieldSrc: 'field',
							field: firstConfig,
							operator: defaultOperator,
							value: [null],
							valueSrc: ['value'],
							valueType: [config?.fields?.[firstConfig]?.type],
						},
					},
				],
			},
		],
	};
};

const getFilterQueryFromSearchParams = ({
	searchParams,
	filterSchema,
	criteria,
}: {
	searchParams: { key: string; value: string | string[] | null | EligibilityRequests.IDateRange };
	filterSchema: IFieldsData[];
	criteria?: JsonTree;
}): JsonTree | null => {
	let newRules: JsonRule = {} as JsonRule;

	const filterQueryResult = filterQuery(criteria);
	const result = _.isEmpty(filterQueryResult)
		? {
				id: Utils.uuid(),
				type: 'group',
				properties: { conjunction: 'OR', not: false },
				children1: [],
			}
		: filterQueryResult;

	if (_.isEmpty(searchParams)) {
		return null;
	}

	const currentSearchConfig = _.find(filterSchema, { field: searchParams.key });
	const rawValue = searchParams.value;

	if (searchParams.key && currentSearchConfig) {
		if (rawValue === null || rawValue === undefined || (_.isArray(rawValue) && !rawValue?.some((v) => hasNonEmptyValue(v)))) {
			return null;
		}

		const baseType = getRqbBaseType(searchParams.key, filterSchema);

		let operator = currentSearchConfig?.searchOperator;
		let processedValue: AnyType[] = [];

		if (operator === OPERATORS.between) {
			const [startDate, endDate] = rawValue as [string, string];
			processedValue = [startDate, endDate];
		} else {
			processedValue = [rawValue];
		}
		if (currentSearchConfig.ui_field_type === QUERY_BUILDER_FILTER_TYPE.minMaxRange) {
			const minValue = rawValue?.[0];
			const maxValue = rawValue?.[1];
			if (minValue && maxValue) {
				processedValue = [minValue, maxValue];
			} else if (minValue) {
				operator = OPERATORS.greaterOrEqual;
				processedValue = [minValue];
			} else if (maxValue) {
				operator = OPERATORS.lessOrEqual;
				processedValue = [maxValue];
			}
		}

		newRules = {
			type: 'rule',
			id: Utils.uuid(),
			properties: {
				fieldSrc: 'field',
				field: searchParams.key,
				operator,
				value: processedValue,
				valueSrc: _.map(processedValue, () => 'value' as ValueSource),
				valueType: _.map(processedValue, () => baseType),
			},
		};
	}

	if (_.isEmpty(newRules)) {
		return result;
	}

	if (_.isEmpty(result.children1)) {
		result.children1 = [
			{
				id: Utils.uuid(),
				type: 'group',
				properties: { conjunction: 'AND', not: false },
				children1: [newRules],
			},
		];
		return result;
	}

	_.map(result.children1, (child) => {
		child.children1.push(newRules);
	});

	return result;
};

const getSearchQueryFromFilterQuery = ({
	searchParams,
}: {
	searchParams: {
		type?: 'search' | 'filter';
		filterParams?: JsonTree;
	};
}): AnyType => {
	const params = _.omitBy(searchParams, _.isNull);
	if (params?.type === 'search' && !_.isEmpty(params?.filterParams)) {
		const groups = searchParams?.filterParams?.children1?.[0];
		const isRule = groups?.type === 'rule';
		const rule = isRule ? groups?.properties : (groups?.children1?.[0] as JsonRule)?.properties;
		const value = rule?.value;
		const field = rule?.field as string;

		const parsedValue = _.isArray(value) ? value : [value];
		return {
			key: field,
			value: _.size(parsedValue) > 1 ? parsedValue : parsedValue[0],
		};
	}
	return null;
};

const getStringifiedQuery = (value: JsonTree, config: Config): AnyType => {
	const immutableTree = Utils.loadTree(value);
	return Utils.queryString(immutableTree, config, true);
};

const updateApiParamsCriteria = <T>(criteria: T, adfFieldMap: Partial<Record<CriteriaKey, string[]>>): T => {
	if (criteria && _.isObject(criteria)) {
		for (const key in criteria) {
			if (key === 'criteria') {
				const object = _.cloneDeep(criteria[key]) as AnyRecord;
				const updatedFilters = buildCriteria(object as JsonTree, adfFieldMap);
				delete criteria[key];
				Object.assign(criteria, updatedFilters);
			} else {
				(criteria as AnyType)[key] = updateApiParamsCriteria((criteria as AnyType)[key], adfFieldMap);
			}
		}
	}
	return criteria;
};

/**
 * Rebuild a QueryBuilder tree from ADF criteria buckets.
 * Reverse of buildCriteria().
 */
const buildQueryFromCriteria = (adfCriteria: Record<CriteriaKey, JsonGroup> | null): JsonTree | null => {
	const criteria = _.cloneDeep(adfCriteria);
	if (!criteria) {
		return null;
	}

	const allRules: AnyType[] = [];

	_.forEach(criteria, (group) => {
		if (!group || !group.children1) {
			return;
		}

		const children = group.children1;

		if (_.isArray(children)) {
			allRules.push(...children);
		} else if (_.isObject(children)) {
			_.forEach(children, (child) => {
				allRules.push(child);
			});
		}
	});

	// If no rules exist
	if (_.isEmpty(allRules)) {
		return null;
	}

	// Return single root group
	return {
		id: Utils.uuid(),
		type: 'group',
		children1: [
			{
				id: Utils.uuid(),
				type: 'group',
				properties: {
					conjunction: 'AND',
					not: false,
				},
				children1: allRules,
			},
		],
		properties: {
			conjunction: 'OR',
			not: false,
		},
	};
};

export {
	getRqbBaseType,
	filterQuery,
	getDefaultQuery,
	getFilterQueryFromSearchParams,
	getSearchQueryFromFilterQuery,
	getStringifiedQuery,
	buildCriteria,
	updateApiParamsCriteria,
	buildQueryFromCriteria,
};
